import type { IInitialEvent, IMfoOfferParams } from './interface';

// Страница ставок

export interface IRatesChartEvent {
  type: string;
  sum: string;
  term: string;
  month: string;
}

export interface IOfferParams {
  id: string;
  sum: string;
  term: string;
  rate: string;
  psk: string;
}

export interface ISendOfferParams {
  calcId: string;
  productId: string;
  organizationId: string;
  organizationName: string;
  productName: string;
  position: number;
  group: string;
}

export const eventRatesChartInit = (data: IRatesChartEvent): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: `Показ компонента ставок`,
  eventLabel: Object.values(data),
  eventValue: undefined,
});

export const eventPaidClick = (data: ISendOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Платный клик',
  eventLabel: [
    data.calcId,
    data.productId,
    data.organizationId,
    data.organizationName,
    data.productName,
    data.position,
    `Листинг - ${data.group}`,
  ].join('|'),
  eventValue: undefined,
});

export const eventSendOffer = (data: ISendOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: `Шаг 5|Заявка отправлена`,
  eventLabel: [
    data.calcId,
    data.productId,
    data.organizationId,
    data.organizationName,
    data.productName,
    data.position,
    `Листинг - ${data.group}`,
  ].join('|'),
  eventValue: undefined,
});

export const eventRatesChartChange = (label: string, value: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: `Компонент ставок|Изменение параметра`,
  eventLabel: [label, value],
  eventValue: undefined,
});

export const eventRatesChartViewItem = (label: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Компонент ставок|Просмотр ставки',
  eventLabel: label,
  eventValue: undefined,
});

export const eventRatesChartSelectItem = (label: string, data: IRatesChartEvent): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Компонент ставок|Переход в подбор',
  eventLabel: [label, ...Object.values(data)],
  eventValue: undefined,
});

// Анкета

export const eventFillFormField = (step: number, fieldName: string, isRequired: boolean): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Заполнение поля',
  eventLabel: [`Шаг ${step}`, fieldName, isRequired ? 'Обязательное' : 'Необязательное'],
  eventValue: undefined,
});

export const eventFormFieldError = (step: number, fieldName: string, isRequired: boolean): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Ошибка заполнения поля',
  eventLabel: [`Шаг ${step}`, fieldName, 'Ошибка валидации', isRequired ? 'Обязательное' : 'Необязательное'],
  eventValue: undefined,
});

export const eventInvalidForm = (
  step: number,
  passType: 'auto' | 'manual',
  fieldName: string,
  fieldValue: string,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Невалидная анкета',
  eventLabel: [`Шаг ${step}`, fieldName, passType === 'auto' ? 'Автопроход' : 'Ручной', fieldValue],
  eventValue: undefined,
});

export const eventChangeFormFieldDefaultParam = (step: number, parameterName: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Изменение параметра',
  eventLabel: [`Шаг ${step}`, parameterName],
  eventValue: undefined,
});

export const eventGetCodeBySms = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Получить код по СМС',
  eventLabel: undefined,
  eventValue: undefined,
});

export const eventInvalidSmsCode = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Неверный код СМС',
  eventValue: undefined,
});

export const eventChangePhoneNumber = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Изменить номер',
  eventValue: undefined,
});

export const eventRequestNewCode = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Получить новый код СМС',
  eventValue: undefined,
});

export const eventAuthorizationError = (source: 'gosuslugi' | 'sravni'): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Ошибка авторизации',
  eventLabel: [source === 'gosuslugi' ? 'Госуслуги' : 'Сравни', 'Лендинг подбора'],
  eventValue: undefined,
});

// В eventLabel передаем способ авторизации:
// - Войти Сравни (авторизация Сравни)
// - Войти Госуслуги (авторизация через Госуслуги)
// - Войти Подбор (авторизация через лендинг)
// - Распознан по номеру (в случае автоматической авторизации)
export const eventAuthorizationSuccess = (source: 'gosuslugi' | 'sravni' | 'landing' | 'header'): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Вход в лк',
  eventLabel: {
    gosuslugi: 'Войти Госуслуги',
    sravni: 'Войти Сравни',
    landing: 'Войти Подбор',
    header: 'Распознан по номеру',
  }[source],
  eventValue: undefined,
});

export const eventShowStep = (step: number, stepName: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: ['Просмотр экрана', `Шаг ${step}`, stepName],
  eventLabel: undefined,
  eventValue: undefined,
});

export const eventStepCreditPurpose = (
  fillType: 'auto' | 'manual',
  stepData: Array<{ fieldLabel: string; fieldValue: string }>,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 1|Цель кредита',
  eventLabel: [
    fillType === 'auto' ? 'Проброс' : 'Ручное',
    ...stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`),
  ],
  eventValue: undefined,
});

export const eventStepContacts = (
  fillType: 'auto' | 'manual',
  stepData: Array<{ fieldLabel: string; fieldValue: string }>,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 2|Контакты',
  eventLabel: [
    fillType === 'auto' ? 'Проброс' : 'Ручное',
    ...stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`),
  ],
  eventValue: undefined,
});

export const eventStepPassport = (
  fillType: 'auto' | 'manual',
  stepData: Array<{ fieldLabel: string; fieldValue: string }>,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 3|Паспорт',
  eventLabel: [
    fillType === 'auto' ? 'Проброс' : 'Ручное',
    ...stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`),
  ],
  eventValue: undefined,
});

export const eventStepWorkplace = (
  fillType: 'auto' | 'manual',
  stepData: Array<{ fieldLabel: string; fieldValue: string }>,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 4|Работа',
  eventLabel: [
    fillType === 'auto' ? 'Проброс' : 'Ручное',
    ...stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`),
  ],
  eventValue: undefined,
});

export const eventStepAdditionalData = (
  fillType: 'auto' | 'manual',
  stepData: Array<{ fieldLabel: string; fieldValue: string }>,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 5|Дополнительные данные',
  eventLabel: [
    fillType === 'auto' ? 'Проброс' : 'Ручное',
    ...stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`),
  ],
  eventValue: undefined,
});

export const eventShowOffers = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Просмотр экрана|Просмотр предложений',
  eventLabel: 'Приоритет - Подбор|Вкладка Подбор',
  eventValue: undefined,
});

export const eventEmptyOffers = (isErrorResponse: boolean, calcId?: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Ничего не найдено',
  eventLabel: isErrorResponse ? 'Ошибка запроса' : `${calcId}|Пустая выдача`,
  eventValue: undefined,
});

export const eventChangeLocation = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Изменение локации',
  eventLabel: 'Изменить город',
  eventValue: undefined,
});

export const eventChangeFilter = (label: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Выбор фильтра',
  eventLabel: label,
  eventValue: undefined,
});

export const eventGoToOfferWebsite = (params: IOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Отправить заявку',
  eventLabel: `Банк' - ${params.id} | 'Сумма' - ${params.sum} | 'Срок' - ${params.term} | 'Ставка' - ${params.rate} | 'ПСК' - ${params.psk}`,
  eventValue: undefined,
});

export const eventOpenConsentsScreen = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Показ',
  eventLabel: 'Согласие на рекламу и обработку',
  eventValue: undefined,
});

export const eventConsentsScreenButtonClick = (
  agreementAdvertising: boolean,
  agreementPersonalData: boolean,
  isSubmitted: boolean,
): IInitialEvent => {
  const eventLabel = ['Согласие на рекламу и обработку'];

  if (isSubmitted) {
    if (agreementPersonalData && !agreementAdvertising) {
      eventLabel.push('Без галочки');
    }
  } else {
    eventLabel.push('Без галочки после клика');
  }

  return {
    event: 'mainEvent',
    eventCategory: 'Подбор кредита',
    eventAction: 'Клик',
    eventLabel,
    eventValue: undefined,
  };
};

export const eventOnDisableAgreementPersonalData = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Согласие на рекламу и обработку | Без галочки | Снял согласие обработки',
  eventValue: undefined,
});

export const eventOnEnableAgreementAdvertising = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Согласие на рекламу и обработку | Поставил галочку',
  eventValue: undefined,
});

export const eventOnShowMplOffersLoadingScreenStep = (scenario: string, loadingStep: number): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Показ',
  eventLabel: `${scenario} | Шаг ${loadingStep}`,
  eventValue: undefined,
});

export const eventOnRatingDescriptionClickOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Клик',
  eventLabel: `${scenario} | Рейтинг`,
  eventValue: undefined,
});

export const eventOnShowRatingDescriptionPopupOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Показ',
  eventLabel: `${scenario} | Какой бывает рейтинг`,
  eventValue: undefined,
});

export const eventOnShowPDNOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Показ',
  eventLabel: `${scenario} | ПДН`,
  eventValue: undefined,
});

export const eventOnShowApprovedOffersPopupOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankApprovalsCount: number;
  notAnsweredBanksCount?: number;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankApprovalsCount, notAnsweredBanksCount } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      `Одобрений - ${bankApprovalsCount}`,
      notAnsweredBanksCount ? `Не все ответили - ${notAnsweredBanksCount}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnCloseApprovedOffersPopupOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankApprovalsCount: number;
  notAnsweredBanksCount?: number;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankApprovalsCount, notAnsweredBanksCount } = data;

  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      `Одобрений - ${bankApprovalsCount}`,
      notAnsweredBanksCount ? `Не все ответили - ${notAnsweredBanksCount}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowApprovedOfferOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  calcId: string;
  productType: string;
  organizationName: string;
  offerIndex: number;
  psk: string;
  sum: string;
  rate: string;
  term: string;
  badges: string;
  status: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    calcId,
    productType,
    organizationName,
    offerIndex,
    psk,
    sum,
    rate,
    term,
    badges,
    status,
  } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      `Тип продукта - ${productType}`,
      organizationName,
      offerIndex,
      psk ? `ПСК - ${psk}` : '',
      sum ? `Сумма - ${sum}` : '',
      rate ? `Ставка - ${rate}` : '',
      term ? `Срок - ${term}` : '',
      badges,
      status,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickApprovedOfferOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  calcId: string;
  productType: string;
  organizationName: string;
  offerIndex: number;
  psk: string;
  sum: string;
  rate: string;
  term: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, calcId, productType, organizationName, offerIndex, psk, sum, rate, term } =
    data;

  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Клик_MPL_Предложение',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      `Тип продукта - ${productType}`,
      offerIndex,
      organizationName,
      psk ? `ПСК - ${psk}` : '',
      sum ? `Сумма - ${sum}` : '',
      rate ? `Ставка - ${rate}` : '',
      term ? `Срок - ${term}` : '',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickWhyThisAmountOnMplCreditSelectionScreen = (scenario: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Клик',
  eventLabel: `${scenario} | Почему такая сумма`,
  eventValue: undefined,
});

export const eventOnClickChangeAmountOnMplCreditSelectionScreen = (
  scenario: string,
  amount: number,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Клик',
  eventLabel: `${scenario} | ${amount} | Изменить`,
  eventValue: undefined,
});

export const eventOnClickApprovalDateOnMplCreditSelectionScreen = (
  scenario: string,
  amount: number,
): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'MarketPlaceLoans',
  eventAction: 'Клик',
  eventLabel: `${scenario} | ${amount} | До какого числа`,
  eventValue: undefined,
});

export const eventOnShowTopXOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  banksCount: number;
  banksNames: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, banksCount, banksNames } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Топ Х',
      `Кол-во банков - ${banksCount}`,
      banksNames,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSubmitClickTopXOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  banksCount: number;
  banksNames: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, banksCount, banksNames } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Клик_topX',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Топ Х',
      `Кол-во банков - ${banksCount}`,
      banksNames,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowRefusedOffersOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  calcId?: string;
  refusedProductIds: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, calcId = '', refusedProductIds = '' } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      refusedProductIds,
      'Почему отказали',
    ].join(' | '),
    eventValue: undefined,
  };
};

// TODO определить, где именно вызвать этот эвент
export const eventOnClickWhyRefusedOffersOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, category, subCategory } = data;
  return {
    event: 'mainEvent',
    eventCategory: 'MarketPlaceLoans',
    eventAction: 'Показ',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, 'Почему отказали'].join(' | '),
    eventValue: undefined,
  };
};

export const eventShowFullDealBlock = ({ mfoName, amount, term, rate }: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Показ блока полной сделки МФО',
  eventLabel: `${mfoName} | ${amount} | ${term} | ${rate}`,
  eventValue: undefined,
});

export const eventApplyForLoan = ({ mfoName, amount, term, rate }: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Оформить займ',
  eventLabel: `${mfoName} | ${amount} | ${term} | ${rate}`,
  eventValue: undefined,
});

export const eventLoanConditions = ({ fillType, amount, term, snils }: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Условия займа',
  eventLabel: `${fillType === 'auto' ? 'Проброс' : 'Ручное'} | ${amount} | ${term} | Снилс ${snils ? 'указан' : 'не указан'}`,
  eventValue: undefined,
});

export const eventCancelSelection = ({ amount, term, snils }: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Отмена',
  eventLabel: `Отмена выбора | ${amount} | ${term} | Снилс ${snils ? 'указан' : 'не указан'}`,
  eventValue: undefined,
});

export const eventSubmitFullDealApplication = ({
  fillType,
  amount,
  term,
  snils,
  mfoName,
  rate,
}: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Отправка заявки на полную сделку',
  eventLabel: `${fillType} | ${mfoName} | ${amount} | ${term} | ${rate} | Снилс ${snils ? 'указан' : 'не указан'}`,
  eventValue: undefined,
});

export const eventMyApplications = ({ mfoName, amount, term, rate }: IMfoOfferParams): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Мои заявки',
  eventLabel: `${mfoName} | ${amount} | ${term} | ${rate}`,
  eventValue: undefined,
});

export const eventButtonClick = (buttonType: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Нажатие на кнопку',
  eventLabel: buttonType,
  eventValue: undefined,
});

export const eventValidationError = ({
  field,
  value,
  reason,
}: {
  field: string;
  value: string;
  reason: string;
}): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Ошибка валидации',
  eventLabel: `${field} | ${value} | ${reason}`,
  eventValue: undefined,
});

export const eventCloseSMSPopupClick = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик',
  eventLabel: 'Закрыть pop-up код из СМС',
  eventValue: undefined,
});

// События лендинга /kredity-new

export const eventShowLandingPage = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Показ',
  eventLabel: 'Показ лендинга',
  eventValue: undefined,
});

export const eventOnClickChangeLocation = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: 'Изменение города',
  eventValue: undefined,
});

export const eventOnContinueButton = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: 'Продолжить',
  eventValue: undefined,
});

export const eventOnClickFillManuallyButton = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: 'Заполнить вручную',
  eventValue: undefined,
});

export const eventOnClickGosuslugiButton = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: 'Кнопка Войти через Госуслуги',
  eventValue: undefined,
});

export const eventOnClickOfferCard = (bankName: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: `Карточка банка|${bankName}`,
  eventValue: undefined,
});

export const eventOnSubmitOffer = (bankName: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: `Перейти на сайт|${bankName}`,
  eventValue: undefined,
});

export const eventOnChangeFilter = (filterName: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита|Лендинг подбора',
  eventAction: 'Клик',
  eventLabel: `Условия от банков|${filterName}`,
  eventValue: undefined,
});

// События баннера биометрии

export const eventClickBiometry = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Клик на виджет биометрии',
  eventLabel: undefined,
  eventValue: undefined,
});

export const eventShowBiometry = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Показ виджета биометрии',
  eventLabel: undefined,
  eventValue: undefined,
});

export const eventGoEsiaFromBiometry = (eventLabel: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Переход с виджета биометрии на Госуслуги',
  eventLabel,
  eventValue: undefined,
});

export const eventFeedClick = (): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Фид|Подробнее|Клик по кнопке',
  eventLabel: 'Подробнее',
  eventValue: undefined,
});

export const eventFeedDecline = ({
  calcId,
  productId,
  organizationId,
  bankName,
  productName,
}: {
  calcId: string;
  productId: string;
  organizationId: string;
  bankName: string;
  productName: string;
}): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 5|Заявка отклонена',
  eventLabel: `${calcId} | ${productId} | ${organizationId} | ${bankName} | ${productName} | Яндекс фид`,
  eventValue: undefined,
});

export const eventFeedSuccess = ({
  calcId,
  productId,
  organizationId,
  bankName,
  productName,
}: {
  calcId: string;
  productId: string;
  organizationId: string;
  bankName: string;
  productName: string;
}): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: 'Шаг 5|Заявка отправлена',
  eventLabel: `${calcId} | ${productId} | ${organizationId} | ${bankName} | ${productName} | Яндекс фид`,
  eventValue: undefined,
});

export const eventFeedFinishClick = (eventAction: string): IInitialEvent => ({
  event: 'mainEvent',
  eventCategory: 'Подбор кредита',
  eventAction: `Фид|${eventAction}|Клик по кнопке`,
  eventLabel: 'Подобрать другой кредит',
  eventValue: undefined,
});
